<template>
  <div
    v-show="containerVisible"
    v-if="containerVisible || !destroy"
    class="ten-drawer__container"
    :style="{ 'z-index': zindex }"
  >
    <template v-if="showMask">
      <transition
        name="ten-drawer-effect"
        appear
      >
        <div
          v-show="drawerVisible"
          ref="mask"
          class="ten-drawer__mask"
          @click="handleMaskClick"
        />
      </transition>
    </template>
    <!-- 抽屉容器 -->
    <transition
      name="ten-drawer-effect"
      appear
    >
      <div
        v-bind="$attrs"
        v-show="drawerVisible"
        v-click-outside="handleOutsideClick"
        class="ten-drawer"
        :class="[`ten-drawer__${position}`]"
        :style="drawerStyle"
      >
        <!-- Resizebar -->
        <div
          v-if="resizable"
          class="ten-drawer__resizebar"
        />
        <!-- Header -->
        <div
          v-if="title && !hideHeader"
          class="ten-drawer__header"
        >
          <div class="ten-drawer__header-title">
            <slot name="title">
              {{ title }}
            </slot>
          </div>
        </div>
        <!-- 关闭按钮 -->
        <ten-icon
          v-if="showCloseButton"
          class="ten-drawer__close"
          icon="clear"
          @click="handleCloseClick"
        />
        <!-- Content -->
        <div class="ten-drawer__body">
          <slot />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Icon from '../icon';
import Emitter from '../scripts/mixins/emitter';
import DomHelper from '../scripts/utils/dom-helper';

import vClickOutside from 'v-click-outside';

const name = 'ten-drawer';
const ANIMATION_DURATION = 240;

let idCounter = 1;
const renderStack = [];

export default {
  name,
  components: {
    TenIcon: Icon,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  mixins: [Emitter],
  model: {
    prop: 'visible',
    event: 'change',
  },
  props: {
    /**
     * @model
     * 是否显示 Drawer
     */
    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * Drawer 标题
     */
    title: String,
    /**
     * Drawer 显示位置
     * @member right | left | top | bottom
     */
    position: {
      type: String,
      default: 'right',
      validator(value) {
        return ['right', 'left', 'top', 'bottom'].indexOf(value) > -1;
      },
    },
    /**
     * Drawer 尺寸。position 为 left / right 时设置为宽度，为 top / bottom 时设置为高度。数字值默认使用像素作为尺寸单位，可传递 px / % 形式的值
     */
    size: {
      type: [Number, String],
      default: 592,
      validator(size) {
        const parsedSize = parseFloat(size);
        const valid = typeof size === 'number' || (!isNaN(parsedSize) && parsedSize >= 0 && parsedSize <= 100);
        return valid;
      },
    },
    /**
     * 手动设置 Drawer 的 z-index
     */
    zIndex: {
      type: Number,
      default: 10000,
    },
    /**
     * 关闭 Drawer 时是否销毁组件
     */
    destroy: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否显示一个遮罩层
     */
    showMask: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否显示关闭按钮
     */
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    /**
     * 显示 Drawer 时，如果页面高度超出 viewport 是否允许页面滚动
     */
    bodyScrollable: {
      type: Boolean,
      default: false,
    },
    /**
     * 是否允许 ESC 按键自动关闭 Drawer
     */
    escClosable: {
      type: Boolean,
      default: true,
    },
    /**
     * 点击 Drawer 面板外部是否关闭面板，只在 showMask=false 时生效
     */
    outsideClickClosable: {
      type: Boolean,
      default: true,
    },
    /**
     * 关闭 Drawer 前执行的回调，返回 false 则放弃关闭 Drawer。（注意这个时间
     */
    beforeClose: Function,
    /**
     * 不显示头部
     */
    hideHeader: Boolean,
    /**
     * 是否启用 Resize 功能
     */
    resizable: {
      type: Boolean,
      default: false,
    },
    /**
     * Drawer 面板最小尺寸
     */
    minSize: {
      type: [Number, String],
      default: 0,
      validator(size) {
        const parsedSize = parseFloat(size);
        const valid = typeof size === 'number' || (!isNaN(parsedSize) && parsedSize >= 0 && parsedSize <= 100);
        return valid;
      },
    },
    /**
     * Drawer 面板最大尺寸
     */
    maxSize: {
      type: [Number, String],
      default: '100%',
      validator(size) {
        const parsedSize = parseFloat(size);
        const valid = typeof size === 'number' || (!isNaN(parsedSize) && parsedSize >= 0 && parsedSize <= 100);
        return valid;
      },
    },
  },
  data() {
    // inner state
    return {
      containerVisible: this.visible,
      drawerVisible: this.visible,
      zindex: this.zIndex,
      // for resizing
      resizing: false,
      dragging: false,
      width: 0,
      height: 0,
      minWidth: 0,
      maxWidth: 0,
      minHeight: 0,
      maxHeight: 0,
      offsetX: 0,
      offsetY: 0,
      mouseX: 0,
      mouseY: 0,
    };
  },
  computed: {
    // computed drawer size
    drawerStyle() {
      if (this.isHorizontal()) {
        return { width: `${this.width}px` };
      }
      return { height: `${this.height}px` };
    },
  },
  watch: {
    // watch visible state
    visible(value) {
      this.setVisible(value, true);
    },
    size(value) {
      this.size = value;
      this.width = this.getWidth();
      this.height = this.getHeight();
    },
    minSize(value) {
      this.minSize = value;
      this.minWidth = this.getMinWidth();
      this.maxWidth = this.getMaxWidth();
      this.minHeight = this.getMinHeight();
      this.maxHeight = this.getMaxHeight();
    },
    maxSize(value) {
      this.maxSize = value;
      this.minWidth = this.getMinWidth();
      this.maxWidth = this.getMaxWidth();
      this.minHeight = this.getMinHeight();
      this.maxHeight = this.getMaxHeight();
    },
    position(value) {
      this.position = value;
      this.width = this.getWidth();
      this.height = this.getHeight();
      this.minWidth = this.getMinWidth();
      this.maxWidth = this.getMaxWidth();
      this.minHeight = this.getMinHeight();
      this.maxHeight = this.getMaxHeight();
    },
  },
  beforeMount() {
    this._id = `ten-drawer-${idCounter}`;
    idCounter += 1;
  },
  mounted() {
    this.width = this.getWidth();
    this.height = this.getHeight();
    this.minWidth = this.getMinWidth();
    this.maxWidth = this.getMaxWidth();
    this.minHeight = this.getMinHeight();
    this.maxHeight = this.getMaxHeight();
    // mounted hook
    this._animationTimeout = 0;
    if (this.visible) {
      document.body.appendChild(this.$el);
    }
  },
  destroyed() {
    // destroyed hook
    try {
      this.$el && document.body.removeChild(this.$el);
    } catch (error) {
    }
  },
  methods: {
    isHorizontal() {
      return ['left', 'right'].indexOf(this.position) > -1;
    },
    getSizeNumber(size) {
      if (typeof size === 'number') {
        return size;
      }
      const parentSize = this.isHorizontal() ? window.innerWidth : window.innerHeight;
      const _size = parentSize * (parseFloat(size) / 100);
      return parseInt(_size, 10);
    },
    getWidth() {
      if (this.isHorizontal()) {
        return this.getSizeNumber(this.size);
      }
      return window.innerWidth;
    },
    getHeight() {
      if (this.isHorizontal()) {
        return window.innerHeight;
      }
      return this.getSizeNumber(this.size);
    },
    getMinWidth() {
      if (this.isHorizontal()) {
        return this.getSizeNumber(this.minSize);
      }
      return window.innerWidth;
    },
    getMaxWidth() {
      if (this.isHorizontal()) {
        return this.getSizeNumber(this.maxSize);
      }
      return window.innerWidth;
    },
    getMinHeight() {
      if (this.isHorizontal()) {
        return window.innerHeight;
      }
      return this.getSizeNumber(this.minSize);
    },
    getMaxHeight() {
      if (this.isHorizontal()) {
        return window.innerHeight;
      }
      return this.getSizeNumber(this.maxSize);
    },
    setBodyCalcSize(clear) {
      const bodyIsScroll =        document.body.scrollHeight
          > (window.innerHeight || document.documentElement.clientHeight)
        && window.innerWidth > document.body.offsetWidth;
      if (!bodyIsScroll) {
        return;
      }
      if (clear) {
        document.body.style.position = '';
        document.body.style.width = '';
        return;
      }
      const calcSize = DomHelper.getBrowserScrollSize();

      if (calcSize) {
        document.body.style.position = 'relative';
        document.body.style.width = `calc(100% - ${calcSize}px)`;
      }
    },

    setVisible(value, initiative) {
      let _isChanged = false;
      // close
      if (!value && this.drawerVisible) {
        if (typeof this.beforeClose === 'function') {
          const result = this.beforeClose();
          if (!result) {
            return;
          }
        }

        /**
         * 关闭回调函数
         */
        this.$emit('close');
        _isChanged = true;
        this.drawerVisible = value;

        clearTimeout(this._animationTimeout);
        this._animationTimeout = setTimeout(() => {
          document.body.style.overflow = '';
          this.setBodyCalcSize(true);
          /**
           * 关闭动画结束回调函数
           */
          this.$emit('closed');
          this.unbindGlobalEvent();
          this.containerVisible = false;

          renderStack.pop();
        }, ANIMATION_DURATION);
      // open
      } else if (value && !this.drawerVisible) {
        _isChanged = true;
        this.drawerVisible = value;
        this.containerVisible = value;
        renderStack.push({
          id: this._id,
        });
        if (renderStack.length > 0) {
          this.zindex = this.zIndex + renderStack.length;
        }
        document.body.appendChild(this.$el);
        this.setBodyCalcSize();
        if (!this.bodyScrollable) {
          document.body.style.overflow = 'hidden';
        }
        /**
         * 显示回调函数
         */
        this.$emit('open');
        clearTimeout(this._animationTimeout);
        this._animationTimeout = setTimeout(() => {
          /**
           * 显示动画结束回调函数
           */
          this.$emit('opened');
          this.bindGlobalEvent();
        }, ANIMATION_DURATION);
      }

      if (_isChanged && !initiative) {
        /**
         * visible 状态改变回调
         */
        this.$emit('change', value);
      }
      this.broadcastAll('do-layout');
    },

    bindGlobalEvent() {
      if (!this.globalEventBinded) {
        this.globalEventBinded = true;
        window.addEventListener('keydown', this.onKeydown);
        if (this.resizable) {
          document.documentElement.addEventListener('mousedown', this.handleDown, true);
        }
      }
    },

    unbindGlobalEvent() {
      this.globalEventBinded = false;
      window.removeEventListener('keydown', this.onKeydown);
      if (this.resizable) {
        document.documentElement.removeEventListener('mousedown', this.handleDown, true);
        document.documentElement.removeEventListener('mouseup', this.handleUp, true);
      }
    },

    handleCloseClick() {
      this.setVisible(false);
    },

    handleMaskClick(event) {
      event.stopPropagation();
      if (this.outsideClickClosable) {
        this.setVisible(false);
      }
    },

    handleOutsideClick(event) {
      event.stopPropagation();
      if (
        !this.drawerVisible
        || (this.showMask && this.outsideClickClosable)
        || event.target.closest('.ten-drawer')
      ) {
        return;
      }
      if (this.outsideClickClosable) {
        // 关闭最顶层 Drawer
        if (renderStack.length > 0) {
          const topDrawer = renderStack[renderStack.length - 1];
          if (this._id === topDrawer.id) {
            this.setVisible(false);
          }
        }
      }
    },

    onKeydown(event) {
      const isEscape = event.keyCode === 27;

      if (this.escClosable && isEscape) {
        this.handleCloseClick();
      }
    },

    // resizing start
    handleDown(event) {
      if (
        this.$el
        && this.$el.nodeType === 1
        && this.$el.contains(event.target)
        && event.target.classList.contains('ten-drawer__resizebar')
      ) {
        event.stopPropagation && event.stopPropagation();
        event.preventDefault && event.preventDefault();
        this.offsetX = 0;
        this.offsetY = 0;
        this.mouseX = event.clientX;
        this.mouseY = event.clientY;
        this.resizing = true;
        document.documentElement.addEventListener('mousemove', this.handleMove, true);
        document.documentElement.addEventListener('mouseup', this.handleUp, true);
        /**
         * resizing 开始
         */
        this.$emit('resizing:start', true);
      }
    },

    // dragging start
    handleMove(event) {
      if (this.resizable && this.resizing) {
        // get offset
        let diffX = event.clientX - this.mouseX + this.offsetX;
        let diffY = event.clientY - this.mouseY + this.offsetY;
        this.offsetX = 0;
        this.offsetY = 0;
        // update style
        // drag to left
        if (this.position === 'right') {
          if (this.width - diffX < this.minWidth) {
            this.offsetX = (diffX - (diffX = this.width - this.minWidth));
          } else if (this.width - diffX > this.maxWidth) {
            this.offsetX = (diffX - (diffX = this.width - this.maxWidth));
          }
          this.width -= diffX;
        }
        // drag to right
        if (this.position === 'left') {
          if (this.width + diffX < this.minWidth) {
            this.offsetX = (diffX - (diffX = this.minWidth - this.width));
          } else if (this.width + diffX > this.maxWidth) {
            this.offsetX = (diffX - (diffX = this.maxWidth - this.width));
          }
          this.width += diffX;
        }
        // drag to bottom
        if (this.position === 'top') {
          if (this.height + diffY < this.minHeight) {
            this.offsetY = (diffY - (diffY = this.minHeight - this.height));
          } else if (this.height + diffY > this.maxHeight) {
            this.offsetY = (diffY - (diffY = this.maxHeight - this.height));
          }
          this.height += diffY;
        }
        // drag to top
        if (this.position === 'bottom') {
          if (this.height - diffY < this.minHeight) {
            this.offsetY = (diffY - (diffY = this.height - this.minHeight));
          } else if (this.height - diffY > this.maxHeight) {
            this.offsetY = (diffY - (diffY = this.height - this.maxHeight));
          }
          this.height -= diffY;
        }
        this.mouseX = event.clientX;
        this.mouseY = event.clientY;

        this.dragging = true;
        /**
         * dragging 进行中
         */
        this.$emit('dragging:start');
      }
    },

    // resizing end && dragging end
    handleUp() {
      if (this.resizable && this.resizing) {
        this.resizing = false;
        /**
         * resizing 结束
         */
        this.$emit('resizing:end');
      }
      if (this.resizable && this.dragging) {
        this.dragging = false;
        document.documentElement.removeEventListener('mousemove', this.handleMove, true);
        /**
         * dragging 结束
         */
        this.$emit('dragging:end');
      }
    },
  },
};
</script>
